import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  AboutTheRoomDetails,
  AboutTheRoomRow,
  AboutTheRoomSection,
  AvailabilityAndPricesSection,
  DetailsSection,
  HostDetailsAbout,
  HostDetailsInfoSection,
  HostDetailsWrapper,
  LeftColumnAbout,
  Location,
  MainBody,
  MainWrapper,
  MapSelectedPlace,
  MapWrapper,
  RightColumnAbout,
  RoomDetailsDescription,
  RoomDetailsSection,
  RoomNavigationSection,
  SectionTitle,
  SocialPreferensecRange,
  TypeOfRoom,
} from "./styles";
import { InfoMessage } from "../HostHouseRegistration/RoomsDetails/styles";
import { Button, IcoArrowRight, IcoInfo, TitleH2, TitleH3, TitleH4, } from "components/generic";

import { AboutMeView, SocialPreferences, } from "pages/ProfileUser/SimpleComponents";
import { useRange } from "components/Range";
import GoogleMapReact from "google-map-react";
import { Carousel } from "./Carousel";
import { HostVerify } from "pages/ProfileUserEdit/HostVerified";
import { IHouse, IUserShot, THouseType, } from "interfaces/intarfaces";
import { API_STUDENT, API_USERS, MONTHS } from "utils/constants";
import { theme } from "styles/theme";
import { UserAPI } from "API/userApi";
import { LoaderCustom } from "components/Loader";
import { StudentAPI } from "API/studentApi";
import { activeYears } from "pages/ProfileUser/StudentAboutInfo";

// map types //

interface IGoogleMap {
  centerCoordinates: TCoordinates;
  zoom: number;
}

type TCoordinates = {
  readonly lat: number;
  readonly lng: number;
};

// MAIN COMPONENT //

export const DetailedRoomInfo: React.FC<{ roomId?: string, setShowRoomPreview?: (show: boolean) => void }> = ({ roomId, setShowRoomPreview }) => {
  const [room_photos, setRoomPhotos] = useState<
    Array<{ file_name: string; url: string; order: number; timestamp: string }>
  >([]);
  const [roomDetailsData, setRoomDetailsData] = useState<
    Partial<IHouse> | undefined
  >(undefined);
  const [hostDetailsData, setHostDetailsData] = useState<Partial<IUserShot>>(
    {}
  );
  const [coordHouse, setCoordHouse] = useState<IGoogleMap | undefined>(
    undefined
  );
  const { t } = useTranslation("student");
  let { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const currentId = roomId ? roomId : id
    StudentAPI.get(API_STUDENT + currentId).then((response) => {
      const housesDetail: any = response;
      if (Object.keys(housesDetail).includes("title")) {
        const { latitude, longitude, photos, ...room } = response;
        setRoomDetailsData(room);
        setRoomPhotos(photos.sort((a: any, b: any) => a.order - b.order) ?? []);
        setCoordHouse({
          centerCoordinates: { lat: latitude, lng: longitude },
          zoom: 15,
        });
      }
    });
  }, [roomId, id]);

  useEffect(() => {
    if (roomDetailsData?.user_id !== undefined) {
      UserAPI.get(API_USERS + roomDetailsData?.user_id + "/user-preview").then(
        (response) => {
          setHostDetailsData(response as IUserShot);
        }
      );
    }
  }, [roomDetailsData]);

  return (
    <MainWrapper>
      {
        setShowRoomPreview && <button id='handle-close' onClick={() => setShowRoomPreview(false)}>&#10005;</button>
      }
      {!roomDetailsData ? (
        <LoaderCustom
          size={5}
          color={theme.colors.bgBody}
          text={t("textLoading.0")}
        />
      ) : (
        <>
          <RoomNavigation
            title={roomDetailsData.title ?? ""}
            street={roomDetailsData.street ?? ""}
            city={roomDetailsData.city ?? ""}
          />
          {room_photos.length ? <Carousel photos={room_photos} /> : ''}
          <MainBody>
            <LeftColumnAbout>
              <DetailsSection>
                <RoomDetails
                  description={roomDetailsData.description ?? ""}
                  kind={(roomDetailsData.kind as THouseType) ?? ""}
                  city={roomDetailsData.city ?? ""}
                  street={roomDetailsData.street ?? ""}
                />
                <AboutTheRoom
                  bedroom_area={roomDetailsData.bedroom_area ?? 0}
                  house_area={roomDetailsData.house_area ?? 0}
                  shared_items={roomDetailsData.shared_items ?? []}
                  private_items={roomDetailsData.private_items ?? []}
                />
                <AvailabilityAndPrices
                  available_from={roomDetailsData.available_from ?? ""}
                  available_to={roomDetailsData.available_to ?? ""}
                  price={roomDetailsData.price ?? 0}
                  additional_expenses={roomDetailsData.additional_expenses ?? 0}
                  deposit={roomDetailsData.deposit ?? 0}
                />
              </DetailsSection>
            </LeftColumnAbout>
            <RightColumnAbout>
              {hostDetailsData === undefined ? (
                ""
              ) : (
                <>
                  <HostDetails
                    verification_status={hostDetailsData.verification_status!}
                    first_name={hostDetailsData.first_name}
                    last_name={hostDetailsData.last_name}
                    active_years={hostDetailsData.active_years ?? 0}
                    hosted_tenants_count={
                      hostDetailsData.hosted_tenants_count ?? 0
                    }
                    thumbnail_url={hostDetailsData.thumbnail_url}
                  />
                  <AboutTheHost
                    gender={hostDetailsData.gender}
                    spoken_languages={hostDetailsData.spoken_languages}
                    description={hostDetailsData.description}
                  />
                  <SocialPreferencesSection
                    privacy={hostDetailsData.privacy ?? 50}
                    requested_autonomy={hostDetailsData.requested_autonomy}
                  />
                </>
              )}
            </RightColumnAbout>
          </MainBody>
          {coordHouse && (
            <GoogleMap
              centerCoordinates={{
                lat: coordHouse.centerCoordinates.lat,
                lng: coordHouse.centerCoordinates.lng,
              }}
              zoom={coordHouse.zoom}
            />
          )}
        </>
      )}
      <Button
        main
        style={{
          alignSelf: "flex-end",
          margin: "1rem",
          padding: "16px 40px",
          fontSize: "18px",
        }}
        onClick={() => {
          if (setShowRoomPreview) {
            setShowRoomPreview(false)
          } else {
            window.history.length > 1 ?
              navigate(-1)
              : window.close()
          }
        }}
      >
        {t("buttonClose")}
      </Button>
    </MainWrapper>
  );
};

// NAVIGATION //

const RoomNavigation: React.FC<Pick<IHouse, "title" | "street" | "city">> = ({
  title,
  street,
  city,
}) => {
  const { t } = useTranslation("student");
  const navigate = useNavigate();
  return (
    <RoomNavigationSection>
      <span className="my-rooms" onClick={() => navigate(-1)}>
        {t("myRooms.pageTitle")}
      </span>
      <IcoArrowRight size=".8em" color={theme.colors.text} />
      <span>{`${title} - ${city}`}</span>
    </RoomNavigationSection>
  );
};

// LEFT COLUMN //

const RoomDetails: React.FC<
  Pick<IHouse, "description" | "kind" | "street" | "city">
> = ({ description, kind, street, city }) => {
  const { t } = useTranslation("hostrooms");

  return (
    <RoomDetailsSection>
      <SectionTitle>
        <TitleH2>{t("roomsDetails.title")}</TitleH2>

      </SectionTitle>
      <RoomDetailsDescription>{description}</RoomDetailsDescription>
      <TypeOfRoom>
        <TitleH3>{t("roomsDetails.typeRoom.title")}</TitleH3>
        <TitleH4>
          {t(`roomsDetails.typeRoom.items.${kind}.title`)}{" "}
          <InfoMessage
            left="-2em"
            message={t(`roomsDetails.typeRoom.items.${kind}.tooltip`)}
          >
            <IcoInfo size="1.2em" />
          </InfoMessage>
        </TitleH4>
      </TypeOfRoom>
      <Location>
        <div>
          <TitleH3>{t("roomsDetails.cityRoom.title")}</TitleH3>
          <TitleH4>{city}</TitleH4>
        </div>
      </Location>
    </RoomDetailsSection>
  );
};

const AboutTheRoom: React.FC<
  Pick<IHouse, "bedroom_area" | "house_area" | "shared_items" | "private_items">
> = ({ bedroom_area, house_area, shared_items, private_items }) => {
  const { t } = useTranslation("hostrooms");

  return (
    <AboutTheRoomSection>
      <SectionTitle>
        <TitleH2>{t("aboutTheRoom.title")}</TitleH2>
      </SectionTitle>
      <AboutTheRoomRow>
        <div>
          <TitleH3>{t("aboutTheRoom.bedroomSize.title")}</TitleH3>
          <TitleH4>{bedroom_area}</TitleH4>
        </div>
        <div>
          <TitleH3>{t("aboutTheRoom.houseSize.title")}</TitleH3>
          <TitleH4>{house_area}</TitleH4>
        </div>
      </AboutTheRoomRow>
      <AboutTheRoomRow>
        <div>
          <TitleH3>{t("aboutTheRoom.sharedUse.title")}</TitleH3>
          {shared_items?.map((item: string, index: number) => (
            <TitleH4 key={index}>
              {t(`aboutTheRoom.sharedUse.items.${item}.title`)}
            </TitleH4>
          ))}
        </div>
        <div>
          <TitleH3>{t("aboutTheRoom.privateUse.title")}</TitleH3>
          {private_items?.map((item: string, index: number) => (
            <TitleH4 key={index}>
              {t(`aboutTheRoom.privateUse.items.${item}.title`)}
            </TitleH4>
          ))}
        </div>
      </AboutTheRoomRow>
    </AboutTheRoomSection>
  );
};

export const AvailabilityAndPrices: React.FC<
  Pick<
    IHouse,
    | "available_from"
    | "available_to"
    | "price"
    | "deposit"
    | "additional_expenses"
  >
> = ({ available_from, available_to, price, deposit, additional_expenses }) => {
  const { t: t_calendar } = useTranslation("translation");
  const { t } = useTranslation("hostrooms");

  const dateLine = (value: string | undefined): string => {
    if (value === undefined || value === null) return "";
    const dt = new Date(value);
    return `${dt.getDate()} ${t_calendar(
      `calendar.months.${MONTHS[dt.getMonth()]}`
    ).toLocaleLowerCase()} ${dt.getFullYear()}`;
  };

  return (
    <AvailabilityAndPricesSection>
      <SectionTitle>
        <TitleH2>{t("availabilityPrices.title")}</TitleH2>
      </SectionTitle>
      <AboutTheRoomDetails>
        <AboutTheRoomRow>
          <div>
            <TitleH3>{t("availabilityPrices.items.dateFrom.title")}</TitleH3>
            <TitleH4>{available_from ? dateLine(available_from) : "-"}</TitleH4>
          </div>
          <div>
            <TitleH3>{t("availabilityPrices.items.dateUntil.title")}</TitleH3>
            <TitleH4>{available_to === '2101-01-01' ? t("anyDate") : available_to ? dateLine(available_to) : "-"}</TitleH4>
          </div>
        </AboutTheRoomRow>
        <AboutTheRoomRow>
          <div>
            <TitleH3>{t("availabilityPrices.items.roomPrice.title")}</TitleH3>
            <TitleH4>{`€ ${price}`}</TitleH4>
          </div>
          <div>
            <TitleH3>{t("availabilityPrices.items.deposit.title")}</TitleH3>
            <TitleH4>{`€ ${deposit}`}</TitleH4>
          </div>
        </AboutTheRoomRow>
        <AboutTheRoomRow>
          <div>
            <TitleH3>
              {t("availabilityPrices.items.additionalCosts.title")}{" "}
              <InfoMessage
                left="-6em"
                message={t("availabilityPrices.items.additionalCosts.tooltip")}
              >
                <IcoInfo size="1.2em" />
              </InfoMessage>
            </TitleH3>
            <TitleH4>{`€ ${additional_expenses}`}</TitleH4>
          </div>
        </AboutTheRoomRow>
      </AboutTheRoomDetails>
    </AvailabilityAndPricesSection>
  );
};

// RIGHT COLUMN //

const HostDetails: React.FC<
  Pick<
    IUserShot,
    | "first_name"
    | "last_name"
    | "active_years"
    | "hosted_tenants_count"
    | "thumbnail_url"
    | "verification_status"
  >
> = ({
  first_name,
  last_name,
  active_years,
  hosted_tenants_count,
  thumbnail_url,
  verification_status,
}) => {
    const { t } = useTranslation("host");
    return (
      <HostDetailsWrapper>
        <SectionTitle>
          <TitleH2>{t("aboutHost.title1")}</TitleH2>
          {verification_status &&
            hosted_tenants_count &&
            hosted_tenants_count > 0 ? (
            <HostVerify verification_status={"experienced"} />
          ) : (
            <span />
          )}
          <HostVerify verification_status={"verified"} showLabel={false} />
        </SectionTitle>
        <HostDetailsInfoSection>
          <HostDetailsAbout>
            <TitleH4>{`${first_name} ${last_name}`}</TitleH4>
            <div>
              <TitleH4>
                <strong>{activeYears(active_years)}</strong>{" "}
                {` ${t("aboutHost.yearsActive")}`}
              </TitleH4>
              <TitleH4>
                <strong>{hosted_tenants_count}</strong>{" "}
                {` ${t("aboutHost.timesHosted")}`}
              </TitleH4>
            </div>
          </HostDetailsAbout>
          <img src={thumbnail_url + `?${new Date().toString()}`} alt="" />
        </HostDetailsInfoSection>
      </HostDetailsWrapper>
    );
  };

const AboutTheHost: React.FC<
  Pick<IUserShot, "description" | "gender" | "spoken_languages">
> = ({ gender, spoken_languages, description }) => {
  const { t } = useTranslation("host");
  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("aboutHost.title2")}</TitleH2>
      </SectionTitle>
      <AboutMeView
        descriptionUser={description}
        genderUser={gender}
        spokenLanguage={spoken_languages}
      />
    </HostDetailsWrapper>
  );
};

const SocialPreferencesSection: React.FC<
  Pick<IUserShot, "privacy" | "requested_autonomy">
> = ({ privacy, requested_autonomy }) => {
  const { t } = useTranslation("host");
  const privacyHost = useRange(privacy);
  const newInTown = useRange(requested_autonomy);

  useEffect(() => {
    privacyHost.onChange(privacy ?? 50);
    newInTown.onChange(requested_autonomy ?? 50);
  }, [privacy, requested_autonomy, privacyHost, newInTown]);

  return (
    <HostDetailsWrapper>
      <SectionTitle>
        <TitleH2>{t("hostProfile.socialPreferences.title")}</TitleH2>
      </SectionTitle>
      <SocialPreferensecRange>
        <SocialPreferences
          roleUser="host"
          privacy={privacyHost}
          newInTown={newInTown}
        />
      </SocialPreferensecRange>
    </HostDetailsWrapper>
  );
};

const GoogleMap: React.FC<IGoogleMap> = (props) => {
  const { centerCoordinates, zoom } = props;

  return (
    <MapWrapper>
      <GoogleMapReact
        defaultCenter={centerCoordinates}
        defaultZoom={zoom}
        bootstrapURLKeys={{
          key: (process.env.REACT_APP_GOOGLE_API as string) || "",
        }}
      >
        <SelectedPlace {...centerCoordinates} />
      </GoogleMapReact>
    </MapWrapper>
  );
};

const SelectedPlace = (props: any) => {
  return (
    <MapSelectedPlace>
      <div />
    </MapSelectedPlace>
  );
};
